import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    Card,
    Table,
    DatePicker,
    Input,
    Button,
    Space,
    Row,
    Col,
    Alert,
    Dropdown,
    Menu,
    Typography,
    message,
    Pagination,
    Modal,
    Select,
    InputNumber,
    Tooltip,
    ConfigProvider,
    theme,
    Checkbox
} from 'antd';
import {
    SearchOutlined,
    DownloadOutlined,
    DeleteOutlined,
    PrinterOutlined,
    FileExcelOutlined,
    BarChartOutlined,
    EyeOutlined,
    EditOutlined,
    FileTextOutlined,
    CalculatorOutlined,
    CheckOutlined,
    PlayCircleOutlined,
    CloseCircleOutlined,
    MoreOutlined,
    InfoCircleOutlined,
    ExportOutlined,
    ScissorOutlined,
    StopOutlined,
    PlusOutlined,
    FilterOutlined,
    ClearOutlined
} from '@ant-design/icons';
import { useTheme } from '../../context/ThemeContext';
import { API_BASE_URL } from '../../utils/config';
import { getNumericFilters, getStringFilters, getDateFilters, formatCellValue, isDateField, applyAdvancedFilter, redirectToPivotWithData, exportToExcel, printTable, parseDate, ActionButtons } from '../../utils/tableFilters';
import { trIncludes } from '../../utils/trText';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isBetween from 'dayjs/plugin/isBetween';
import * as XLSX from 'xlsx';
import FormikForm from '../form/FormikForm';
import '../fatura/FaturaPage.css';
import { useNavigate } from 'react-router-dom';
import Chatbot from '../giris/Chatbot.jsx';

dayjs.extend(customParseFormat);
dayjs.extend(isBetween);

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const monthNames = [
    'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
    'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
];


const DemirbasYonetimiPage = () => {
    const navigate = useNavigate();
    const { darkMode } = useTheme();
    const [activeTab, setActiveTab] = useState('demirbas-liste');
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({
        dateRange: null,
        search: '',
        page: 1,
        pageSize: 50,
    });
    const [pagination, setPagination] = useState({
        current: 1,
        total: 0,
        pageSize: 10
    });
    const [formModalOpen, setFormModalOpen] = useState(false);
    const [currentFormId, setCurrentFormId] = useState('');
    const [modalTitle, setModalTitle] = useState('');
    const [formTableName, setFormTableName] = useState('');
    const [formUniqueField, setFormUniqueField] = useState(null);
    const [currentUniqueValue, setCurrentUniqueValue] = useState(null);
    const [detailModalOpen, setDetailModalOpen] = useState(false);
    const [detailData, setDetailData] = useState([]);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState(null);
    const [currentRecordId, setCurrentRecordId] = useState(null);
    const [modalButtonType, setModalButtonType] = useState('main-table');
    const [filteredInfo, setFilteredInfo] = useState({});
    const [sortedInfo, setSortedInfo] = useState({});
    const [columns, setColumns] = useState([]);
    const [showData, setShowData] = useState([]);
    const [selectedColumns, setSelectedColumns] = useState([]);
    const [columnSelectDropdownOpen, setColumnSelectDropdownOpen] = useState(false);
    const loadDataRef = useRef(null);

    useEffect(() => {
        if (darkMode) {
            document.body.setAttribute('data-theme', 'dark');
        } else {
            document.body.removeAttribute('data-theme');
        }
    }, [darkMode]);

    const openFormForRecord = (record, idField, formId, title, tableName) => {
        const id = record[idField];
        if (id) {
            handleOpenForm(formId, title, tableName, 'id', id);
        } else {
            message.error('ID bulunamadı');
        }
    };

    const demirbasActionButtons = {
        'KEY_ZIMMET': [
            { key: 'edit', label: 'Düzenle', type: 'default', icon: 'EditOutlined', onClick: (record) => openFormForRecord(record, 'id', 101, 'Demirbas Düzenle', 'demirbas') },
            { key: 'add', label: 'Fatura Ekle', type: 'default', icon: 'PlusOutlined', onClick: (record) => openFormForRecord(record, 'id', 102, 'Demirbas Fatura Ekleme Formu', 'demirbas_dosya') },
            { key: 'view', label: 'Görüntüle', type: 'default', icon: 'EyeOutlined', onClick: (record) => handleOpenDetailModal(record, null, 'sub-table') },
        ],
        'KEY_DEMIRBAS_ZIMMET': [
            { key: 'view', label: 'Görüntüle', type: 'default', icon: 'EyeOutlined', onClick: (record) => handleOpenDetailModal(record, null, 'main-table') },
        ],
    };

    const demirbasTabs = [
        {
            key: 'demirbas-liste',
            label: 'Demirbaş Liste',
            sqlKey: 'demirbas-liste',
            formManagerId: 26,
            actionButtons: demirbasActionButtons,
        },
        {
            key: 'demirbas-kullanilan',
            label: 'Kullanılan Demirbaşlar',
            sqlKey: 'demirbas-kullanilan',
            formManagerId: 159,
            actionButtons: demirbasActionButtons,
        },
        {
            key: 'demirbas-kullanilmayan',
            label: 'Kullanılmayan Demirbaşlar',
            sqlKey: 'demirbas-kullanilmayan',
            formManagerId: 160,
            actionButtons: demirbasActionButtons,
        },
        {
            key: 'demirbas-pasif',
            label: 'Pasif Demirbaşlar',
            sqlKey: 'demirbas-pasif',
            formManagerId: 161,
            actionButtons: demirbasActionButtons,
        },
        {
            key: 'zimmet-liste',
            label: 'Zimmetler',
            sqlKey: 'zimmet-liste',
            formManagerId: 27,
            actionButtons: {
                'KEY_ZIMMET': [
                    { key: 'add', label: 'Teslimat Ekle', type: 'default', icon: 'PlusOutlined', onClick: (record) => openFormForRecord(record, 'No', 100, 'Zimmet Teslim Formu', 'zimmet') },
                    { key: 'edit', label: 'Düzenle', type: 'default', icon: 'EditOutlined', onClick: (record) => openFormForRecord(record, 'No', 98, 'Zimmet Düzenle', 'zimmet') },
                    { key: 'view', label: 'Görüntüle', type: 'default', icon: 'EyeOutlined', onClick: (record) => handleOpenDetailModal(record, null, 'sub-table') },
                ],
                'KEY_DEMIRBAS_ZIMMET': [
                    { key: 'view', label: 'Görüntüle', type: 'default', icon: 'EyeOutlined', onClick: (record) => handleOpenDetailModal(record, null, 'main-table') },
                ],
            },
        },
    ];

    const calculateDateRange = (tabData) => {
        if (!tabData.dateRange) return null;

        const { startOffset, endOffset, unit } = tabData.dateRange;
        const today = dayjs();

        const startDate = today.add(startOffset, unit);
        const endDate = today.add(endOffset, unit);

        return [startDate, endDate];
    };

    const loadData = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const activeTabData = demirbasTabs.find(tab => tab.key === activeTab);
            if (!activeTabData) {
                console.warn('Active tab data not found for key:', activeTab);
                return;
            }
            const urlParams = new URLSearchParams();

            let url, response;

            url = `${API_BASE_URL}/list/execute/${activeTabData.sqlKey}${urlParams.toString() ? '?' + urlParams.toString() : ''}`;
            response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error(`API Error - Status: ${response.status}, Response:`, errorText);
                throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
            }

            const allData = await response.json();
            if (Array.isArray(allData) && allData.length > 0) {
                const dateFields = Object.keys(allData[0]).filter(key =>
                    key.toLowerCase().includes('tarih') ||
                    key.toLowerCase().includes('tar') ||
                    key.toLowerCase().includes('başlangıç') ||
                    key.toLowerCase().includes('bitiş') ||
                    key.toLowerCase().includes('start') ||
                    key.toLowerCase().includes('end') ||
                    key.toLowerCase().includes('dönem')
                );
            }

            if (!Array.isArray(allData)) {
                console.warn('API response is not an array:', allData);
                setError('Geçersiz veri formatı - API array döndürmüyor');
                return;
            }

            let filteredData = [...allData];

            const shouldApplyDateFilter = !activeTabData.noDateFilter;

            const dynamicDateRange = shouldApplyDateFilter &&
                !(activeTabData.dateRange && activeTabData.dateRange.noDefaultDate)
                ? calculateDateRange(activeTabData) : null;

            const effectiveDateRange = filters.dateRange || dynamicDateRange;

            if (shouldApplyDateFilter && effectiveDateRange && effectiveDateRange[0] && effectiveDateRange[1]) {
                const filterStartDate = effectiveDateRange[0].startOf('day');
                const filterEndDate = effectiveDateRange[1].endOf('day');
                filteredData = filteredData.filter(record => {
                    const satinAlinmaTarihi = record['Satın Alınma Tarihi'];

                    if (!satinAlinmaTarihi) {
                        return false;
                    }

                    let recordDate;
                    const dateValue = String(satinAlinmaTarihi).trim();

                    if (dateValue.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
                        recordDate = dayjs(dateValue, 'DD.MM.YYYY');
                    } else if (dateValue.match(/^\d{4}\.\d{2}\.\d{2}$/)) {
                        recordDate = dayjs(dateValue, 'YYYY.MM.DD');
                    } else if (dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
                        recordDate = dayjs(dateValue, 'YYYY-MM-DD');
                    } else {
                        recordDate = dayjs(satinAlinmaTarihi);
                    }

                    if (recordDate.isValid()) {
                        return recordDate.isBetween(filterStartDate, filterEndDate, null, '[]');
                    }

                    return false;
                });
            }
            if (filters.search && filters.search.trim() !== '') {
                const searchTerm = filters.search.trim();
                filteredData = filteredData.filter(record => {
                    return Object.values(record).some(value => {
                        if (value === null || value === undefined) return false;
                        return trIncludes(String(value), searchTerm);
                    });
                });
            }

            if (filteredData.length > 0) {
                const sample = filteredData[0];
                const dynamicCols = Object.keys(sample)
                    .filter((key) => !key.toLowerCase().includes('fatura') &&
                        !key.toLowerCase().includes('türü') &&
                        !key.toLowerCase().includes('key_depo') &&
                        !key.toLowerCase().includes('key_kabin_islem'))
                    .map((key) => {
                        const firstValue = sample[key];
                        const isDateColumn = isDateField(key);
                        const isNumber = (!isDateColumn && (
                            (typeof firstValue === "number" && !isNaN(firstValue)) ||
                            (typeof firstValue === "string" && (
                                firstValue.match(/^₺?[\d.,]+$/) ||
                                firstValue.match(/^\$?[\d.,]+$/) ||
                                firstValue.match(/^€?[\d.,]+$/) ||
                                firstValue.match(/^[\d.,]+$/) ||
                                key.toLowerCase().includes('tutar') ||
                                key.toLowerCase().includes('fiyat') ||
                                key.toLowerCase().includes('ücret') ||
                                key.toLowerCase().includes('miktar') ||
                                key.toLowerCase().includes('adet') ||
                                key.toLowerCase().includes('kesilen') ||
                                key.toLowerCase().includes('toplam')
                            )))
                        );
                        const uniqueValues = [...new Set(filteredData.map((item) => item[key]))].filter(
                            (val) => val !== undefined && val !== null && val !== ""
                        );

                        let filters = [];
                        if (isNumber) {
                            filters = getNumericFilters();
                        } else if (isDateColumn) {
                            filters = getDateFilters();
                        } else {
                            filters = getStringFilters();
                        }

                        return {
                            key: key,
                            title: key,
                            dataIndex: key,
                            isDateColumn,
                            isNumber,
                            isString: !isNumber && !isDateColumn,
                            uniqueValues,
                            sortable: true,
                            filters,
                        };
                    });
                setColumns(dynamicCols);
            }
            setData(filteredData || []);
            setShowData(filteredData || []);
            setPagination(prev => ({
                ...prev,
                total: filteredData?.length || 0
            }));

        } catch (err) {
            console.error('Demirbas data load error:', err);
            setError('Veri yüklenirken hata oluştu: ' + err.message);
        } finally {
            setLoading(false);
        }
    }, [activeTab, filters.dateRange, filters.search]);

    const handlePivotRedirect = useCallback(() => {
        const activeTabData = demirbasTabs.find(tab => tab.key === activeTab);
        if (!activeTabData?.sqlKey) return;
        redirectToPivotWithData({
            data,
            filters,
            tabLabel: activeTabData.label || 'Demirbaş',
            sqlKey: activeTabData.sqlKey,
            navigate,
            message,
            setLoading,
            apiBaseUrl: API_BASE_URL,
        });
    }, [activeTab, data, filters, navigate]);

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value
        }));
        setPagination(prev => ({
            ...prev,
            current: 1
        }));
    };

    const handleTabChange = (key) => {
        setActiveTab(key);
        const newTabData = demirbasTabs.find(tab => tab.key === key);

        const shouldSetDefaultDate = newTabData &&
            !newTabData.noDateFilter &&
            !(newTabData.dateRange && newTabData.dateRange.noDefaultDate);
        const dynamicDateRange = shouldSetDefaultDate ? calculateDateRange(newTabData) : null;
        const defaultYear = newTabData && newTabData.dateType === 'yeartime' ?
            (newTabData.defaultYear || new Date().getFullYear()) : null;
        const defaultMonth = newTabData && newTabData.dateType === 'yeartime' ?
            (newTabData.defaultMonth || new Date().getMonth() + 1) : null;

        setFilters({
            dateRange: dynamicDateRange,
            search: '',
            durum: null,
            firma: null,
            page: 1,
            pageSize: 25,
            year: defaultYear,
            month: defaultMonth
        });
        setPagination({
            current: 1,
            total: 0,
            pageSize: 10
        });
    };

    const handlePageChange = (page, pageSize) => {
        setPagination(prev => ({
            ...prev,
            current: page,
            pageSize: pageSize || prev.pageSize
        }));
    };

    const handleOpenForm = (formId, title = 'Form', tableName = '', uniqueField = '', uniqueValue = null) => {
        setModalTitle(title);
        setCurrentFormId(formId);
        setFormTableName(tableName);
        setFormUniqueField(uniqueField);
        setCurrentUniqueValue(uniqueValue);
        setFormModalOpen(true);
    };

    const handleFormSuccess = () => {
        setFormModalOpen(false);
        setCurrentFormId(null);
        setFormTableName('');
        setFormUniqueField('');
        loadData();
    };

    const closeFormModal = () => {
        setFormModalOpen(false);
    };

    const handleOpenDetailModal = async (record, firstChar = null, modalType = 'main-table') => {
        const id = record.key_demirbas_zimmet || record.id || record.No;
        if (!id) {
            message.error('ID bulunamadı');
            return;
        }

        const idString = String(id);
        setCurrentRecordId(idString);
        setDetailModalOpen(true);
        setDetailLoading(true);
        setDetailError(null);
        setModalButtonType(modalType);

        try {
            const charToUse = firstChar || idString.charAt(0).toLowerCase();
            let queryName = 'demirbas';
            let modalTitle = 'Demirbaş Detayları';

            if (modalType === 'sub-table') {
                queryName = 'demirbas';
                modalTitle = 'Demirbaş Detayları';
            } else {
                switch (charToUse) {
                    case 'k':
                        modalTitle = 'Kredi Kartı Zimmet Detayları';
                        queryName = 'zimmet-detail-k';
                        break;
                    case 'a':
                        modalTitle = 'Araç Zimmet Detayları';
                        queryName = 'zimmet-detail-a';
                        break;
                    case 'd':
                        modalTitle = 'Demirbaş Zimmet Detayları';
                        queryName = 'zimmet-detail-d';
                        break;
                    default:
                        queryName = 'demirbas';
                        modalTitle = 'Demirbaş Detayları';
                        break;
                }
            }

            let paramId = idString;
            if (modalType === 'sub-table' && queryName === 'demirbas') {
                paramId = record.demirbas_id || record.id || idString;
            }

            const response = await fetch(`${API_BASE_URL}/list/execute-query/${queryName}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ params: { id: paramId } })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            setDetailData(Array.isArray(data) ? data : []);

            setModalTitle(modalTitle);
        } catch (err) {
            console.error('Detail modal data load error:', err);
            setDetailError('Detay verileri yüklenirken hata oluştu: ' + err.message);
        } finally {
            setDetailLoading(false);
        }
    };

    const closeDetailModal = () => {
        setDetailModalOpen(false);
        setDetailData([]);
        setDetailError(null);
        setCurrentRecordId(null);
        setModalButtonType('main-table');
    };

    const handleViewFile = (record) => {
        const id = record.file;
        if (!id) {
            message.error('ID bulunamadı');
            return;
        }
        const MINIO_BASE_URL = 'https://dosya.atmturkiye.com';
        const BUCKET_NAME = 'atmpoint';
        const pathSegments = id.split('/');
        const encodedSegments = pathSegments.map(segment => {
            return encodeURIComponent(segment);
        });
        const encodedPath = encodedSegments.join('/');
        const fileUrl = `${MINIO_BASE_URL}/${BUCKET_NAME}/${encodedPath}`;
        window.open(fileUrl, '_blank');
    };

    const handleEditFile = (record) => {
        const id = record.No;
        if (!id) {
            message.error('ID bulunamadı');
            return;
        }
        handleOpenForm(98, 'Demirbaş Dosya Düzenle', 'zimmet', 'id', id);
    };

    const handleSubEditFile = (record) => {
        const id = record.dosyaID;
        if (!id) {
            message.error('ID bulunamadı');
            return;
        }
        handleOpenForm(103, 'Demirbaş Dosya Düzenle', 'demirbas_dosya', 'id', id);
    };

    const handleAddDelivery = (record) => {
        const id = record.No;
        if (!id) {
            message.error('ID bulunamadı');
            return;
        }
        handleOpenForm(100, 'Zimmet Teslim Formu', 'zimmet', 'id', id);
    };

    const handleAdvancedFilterChange = (columnKey, valueType, value) => {
        setFilteredInfo((prev) => ({
            ...prev,
            [columnKey]: {
                ...prev[columnKey],
                [valueType]: value,
            },
        }));
        setPagination(prev => ({
            ...prev,
            current: 1
        }));
    };

    const clearColumnFilter = (columnKey) => {
        setFilteredInfo((prev) => {
            const newFilteredInfo = { ...prev };
            delete newFilteredInfo[columnKey];
            return newFilteredInfo;
        });
        setPagination(prev => ({
            ...prev,
            current: 1
        }));
    };

    const clearAllFilters = useCallback(() => {
        setFilteredInfo({});
        setFilters({
            dateRange: null,
            search: '',
            page: 1,
            pageSize: 50
        });
        setSortedInfo({});
        setPagination(prev => ({
            ...prev,
            current: 1
        }));
    }, []);

    const handleSort = (columnKey) => {
        const column = columns.find((col) => col.key === columnKey);
        if (!column) return;

        let newOrder = "asc";
        if (sortedInfo.columnKey === columnKey) {
            if (sortedInfo.order === "asc") {
                newOrder = "desc";
            } else if (sortedInfo.order === "desc") {
                newOrder = null;
            }
        }

        setSortedInfo({
            columnKey: newOrder ? columnKey : null,
            order: newOrder,
        });
    };

    const filteredAndSortedData = useMemo(() => {
        let result = data.filter((item) => {
            const matchesSearch = Object.values(item).some((val) =>
                trIncludes(String(val), filters.search)
            );

            const matchesFilters = Object.entries(filteredInfo).every(([key, filter]) => {
                const recordValue = item[key];
                const col = columns.find((c) => c.key === key);
                return applyAdvancedFilter(recordValue, filter, col);
            });

            return matchesSearch && matchesFilters;
        });

        if (sortedInfo.columnKey && sortedInfo.order) {
            const column = columns.find((col) => col.key === sortedInfo.columnKey);
            if (column) {
                result.sort((a, b) => {
                    const valA = a[sortedInfo.columnKey];
                    const valB = b[sortedInfo.columnKey];

                    if (column.isDateColumn) {
                        let dateA = null, dateB = null;

                        if (valA) {
                            const dateValueA = String(valA).trim();
                            if (dateValueA.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
                                dateA = dayjs(dateValueA, 'DD.MM.YYYY');
                            } else if (dateValueA.match(/^\d{4}-\d{2}-\d{2}$/)) {
                                dateA = dayjs(dateValueA, 'YYYY-MM-DD');
                            } else {
                                dateA = dayjs(valA);
                            }
                        }

                        if (valB) {
                            const dateValueB = String(valB).trim();
                            if (dateValueB.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
                                dateB = dayjs(dateValueB, 'DD.MM.YYYY');
                            } else if (dateValueB.match(/^\d{4}-\d{2}-\d{2}$/)) {
                                dateB = dayjs(dateValueB, 'YYYY-MM-DD');
                            } else {
                                dateB = dayjs(valB);
                            }
                        }

                        if (!dateA && !dateB) return 0;
                        if (!dateA || !dateA.isValid()) return sortedInfo.order === "asc" ? -1 : 1;
                        if (!dateB || !dateB.isValid()) return sortedInfo.order === "asc" ? 1 : -1;
                        const comparison = dateA.isBefore(dateB) ? -1 : dateA.isAfter(dateB) ? 1 : 0;
                        return sortedInfo.order === "asc" ? comparison : -comparison;
                    } else if (column.isNumber) {
                        const numA = Number(valA) || 0;
                        const numB = Number(valB) || 0;
                        return sortedInfo.order === "asc" ? numA - numB : numB - numA;
                    } else {
                        const strA = String(valA || "").toLowerCase();
                        const strB = String(valB || "").toLowerCase();
                        const comparison = strA.localeCompare(strB, 'tr');
                        return sortedInfo.order === "asc" ? comparison : -comparison;
                    }
                });
            }
        }

        return result;
    }, [data, filters.search, filteredInfo, sortedInfo, columns]);

    const paginatedData = useMemo(() => {
        const startIndex = (pagination.current - 1) * pagination.pageSize;
        return filteredAndSortedData.slice(startIndex, startIndex + pagination.pageSize);
    }, [filteredAndSortedData, pagination.current, pagination.pageSize]);

    const saveSelectedColumnsToStorage = useCallback((columnKeys) => {
        try {
            const storageKey = `demirbas_selected_columns_${activeTab}`;
            localStorage.setItem(storageKey, JSON.stringify(columnKeys));
        } catch (error) {
            console.error('Error saving selected columns to storage:', error);
        }
    }, [activeTab]);

    useEffect(() => {
        if (columns.length > 0) {
            try {
                const storageKey = `demirbas_selected_columns_${activeTab}`;
                const saved = localStorage.getItem(storageKey);
                if (saved) {
                    const parsed = JSON.parse(saved);
                    const validColumns = parsed.filter(key => columns.some(col => col.key === key));
                    if (validColumns.length > 0) {
                        setSelectedColumns(validColumns);
                    } else {
                        const allColumnKeys = columns.map(col => col.key);
                        setSelectedColumns(allColumnKeys);
                        saveSelectedColumnsToStorage(allColumnKeys);
                    }
                } else {
                    const allColumnKeys = columns.map(col => col.key);
                    setSelectedColumns(allColumnKeys);
                    saveSelectedColumnsToStorage(allColumnKeys);
                }
            } catch (error) {
                console.error('Error loading selected columns from storage:', error);
                const allColumnKeys = columns.map(col => col.key);
                setSelectedColumns(allColumnKeys);
            }
        }
    }, [columns, saveSelectedColumnsToStorage, activeTab]);

    const handleColumnSelectionChange = (checkedValues) => {
        setSelectedColumns(checkedValues);
        saveSelectedColumnsToStorage(checkedValues);
    };

    const handleSelectAllColumns = (checked) => {
        if (checked) {
            const allColumnKeys = columns.map(col => col.key);
            setSelectedColumns(allColumnKeys);
            saveSelectedColumnsToStorage(allColumnKeys);
        } else {
            setSelectedColumns([]);
            saveSelectedColumnsToStorage([]);
        }
    };

    const visibleColumns = useMemo(() => {
        if (selectedColumns.length === 0) {
            return columns;
        }
        return columns.filter(col => selectedColumns.includes(col.key));
    }, [columns, selectedColumns]);

    const removeColumn = (columnKey) => {
        setSelectedColumns((prev) => {
            const newSelected = prev.filter((key) => key !== columnKey);
            saveSelectedColumnsToStorage(newSelected);
            return newSelected;
        });
    };

    const getFilterDropdown = (column) => {
        const currentFilter = filteredInfo[column.key] || {};
        const condition = currentFilter.condition || "choose";

        return (
            <div style={{ padding: 16, minWidth: 300 }}>
                <Space direction="vertical" style={{ width: '100%', backgroundColor: darkMode ? 'var(--dark-bg)' : 'white', border: `1px solid ${darkMode ? 'var(--dark-border)' : '#d9d9d9'}`, padding: 12 }}>
                    <div>
                        <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : '#000' }}>
                            Filtre Şartı
                        </Text>
                        <Select
                            size="small"
                            value={condition}
                            onChange={(value) => handleAdvancedFilterChange(column.key, "condition", value)}
                            style={{ width: '100%' }}
                        >
                            {column.filters.map((filter) => (
                                <Option key={filter.value} value={filter.value}>
                                    {filter.text}
                                </Option>
                            ))}
                        </Select>
                    </div>

                    {/* String değer seçimi */}
                    {column.isString && condition === "select" && (
                        <div>
                            <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : '#000' }}>
                                Değer Seç
                            </Text>
                            <Select
                                size="small"
                                value={currentFilter.value || ""}
                                onChange={(value) => handleAdvancedFilterChange(column.key, "value", value)}
                                style={{ width: '100%' }}
                                placeholder="Seçiniz"
                            >
                                <Option value="">Seçiniz</Option>
                                {column.uniqueValues.map((val) => (
                                    <Option key={val} value={val}>
                                        {val}
                                    </Option>
                                ))}
                            </Select>
                        </div>
                    )}

                    {/* Sayısal filtreler için input alanları */}
                    {column.isNumber && condition !== "choose" && condition !== "between" && (
                        <div>
                            <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : '#000' }}>
                                Değer
                            </Text>
                            <InputNumber
                                size="small"
                                placeholder="Değer giriniz"
                                value={currentFilter.value || ""}
                                onChange={(value) => handleAdvancedFilterChange(column.key, "value", value)}
                                style={{ width: '100%' }}
                            />
                        </div>
                    )}

                    {/* Sayısal arasında filtresi */}
                    {column.isNumber && condition === "between" && (
                        <>
                            <div>
                                <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : '#000' }}>
                                    Min Değer
                                </Text>
                                <InputNumber
                                    size="small"
                                    placeholder="Min"
                                    value={currentFilter.min || ""}
                                    onChange={(value) => handleAdvancedFilterChange(column.key, "min", value)}
                                    style={{ width: '100%' }}
                                />
                            </div>
                            <div>
                                <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : '#000' }}>
                                    Max Değer
                                </Text>
                                <InputNumber
                                    size="small"
                                    placeholder="Max"
                                    value={currentFilter.max || ""}
                                    onChange={(value) => handleAdvancedFilterChange(column.key, "max", value)}
                                    style={{ width: '100%' }}
                                />
                            </div>
                        </>
                    )}

                    {/* String filtreler için input alanları */}
                    {column.isString && !["choose", "empty", "notEmpty", "select"].includes(condition) && (
                        <div>
                            <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : '#000' }}>
                                Değer
                            </Text>
                            <Input
                                size="small"
                                placeholder="Değer giriniz"
                                value={currentFilter.value || ""}
                                onChange={(e) => handleAdvancedFilterChange(column.key, "value", e.target.value)}
                                style={{ width: '100%' }}
                            />
                        </div>
                    )}

                    {/* Tarih filtreler için input alanları */}
                    {column.isDateColumn && ["equals", "before", "after"].includes(condition) && (
                        <div>
                            <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : '#000' }}>
                                Tarih
                            </Text>
                            <DatePicker
                                size="small"
                                value={currentFilter.value ? dayjs(currentFilter.value, 'DD.MM.YYYY') : null}
                                onChange={(date) => handleAdvancedFilterChange(column.key, "value", date ? date.format('DD.MM.YYYY') : null)}
                                style={{ width: '100%' }}
                                format="DD.MM.YYYY"
                            />
                        </div>
                    )}

                    {/* Tarih arasında filtresi */}
                    {column.isDateColumn && condition === "between" && (
                        <>
                            <div>
                                <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : '#000' }}>
                                    Başlangıç Tarihi
                                </Text>
                                <DatePicker
                                    size="small"
                                    value={currentFilter.startDate ? dayjs(currentFilter.startDate, 'DD.MM.YYYY') : null}
                                    onChange={(date) => handleAdvancedFilterChange(column.key, "startDate", date ? date.format('DD.MM.YYYY') : null)}
                                    style={{ width: '100%' }}
                                    format="DD.MM.YYYY"
                                />
                            </div>
                            <div>
                                <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : '#000' }}>
                                    Bitiş Tarihi
                                </Text>
                                <DatePicker
                                    size="small"
                                    value={currentFilter.endDate ? dayjs(currentFilter.endDate, 'DD.MM.YYYY') : null}
                                    onChange={(date) => handleAdvancedFilterChange(column.key, "endDate", date ? date.format('DD.MM.YYYY') : null)}
                                    style={{ width: '100%' }}
                                    format="DD.MM.YYYY"
                                />
                            </div>
                        </>
                    )}

                    <Button
                        size="small"
                        onClick={() => clearColumnFilter(column.key)}
                        style={{ marginTop: 8, width: '100%' }}
                    >
                        Filtreyi Sıfırla
                    </Button>
                </Space>
            </div>
        );
    };

    useEffect(() => {
        loadDataRef.current = loadData;
    }, [loadData]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    return (
        <div className={`fatura-page ${darkMode ? 'dark-theme' : ''}`}>
            {/* Filtreler */}
            <Card
                className={`fatura-filters-card ${darkMode ? 'dark-card' : ''}`}
                style={{
                    marginTop: 16,
                    backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : undefined,
                    borderColor: darkMode ? 'var(--dark-border)' : undefined
                }}
            >
                <Row gutter={[16, 16]} align="middle">
                    <Col xs={24} sm={12} md={6} lg={4}>
                        <div className="filter-item">
                            <Text strong style={{ color: darkMode ? 'var(--dark-text)' : undefined }}>
                                Demirbaş Türü
                                <EditOutlined onClick={() => {
                                    const activeTabData = demirbasTabs.find(tab => tab.key === activeTab);
                                    const formManagerId = activeTabData?.formManagerId;
                                    handleOpenForm(275, 'Liste Formu', 'sqls', 'id', formManagerId);
                                }} />
                            </Text>
                            <Select
                                placeholder="Demirbaş Türü Seçin" s
                                value={activeTab}
                                onChange={(value) => handleTabChange(value)}
                                style={{ width: '100%' }}
                                className={darkMode ? 'dark-select' : ''}
                            >
                                {demirbasTabs.map(tab => (
                                    <Option key={tab.key} value={tab.key}>
                                        {tab.label}
                                    </Option>
                                ))}
                            </Select>
                        </div>
                    </Col>

                    {/* Tarih Aralığı veya Yıl/Ay Seçici */}
                    {(() => {
                        const activeTabData = demirbasTabs.find(tab => tab.key === activeTab);

                        if (activeTabData && activeTabData.dateType === 'yeartime') {
                            return (
                                <>
                                    <Col xs={12} sm={6} md={3} lg={2}>
                                        <div className="filter-item">
                                            <Text strong style={{ color: darkMode ? 'var(--dark-text)' : undefined }}>
                                                Yıl
                                            </Text>
                                            <Select
                                                value={filters.year}
                                                onChange={(value) => handleFilterChange('year', value)}
                                                style={{
                                                    width: '100%',
                                                    backgroundColor: darkMode ? 'var(--dark-bg)' : undefined,
                                                    borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                                    color: darkMode ? 'var(--dark-text)' : undefined
                                                }}
                                                className={darkMode ? 'dark-select' : ''}
                                            >
                                                {Array.from({ length: 10 }, (_, i) => {
                                                    const year = new Date().getFullYear() - 5 + i;
                                                    return (
                                                        <Option key={year} value={year}>{year}</Option>
                                                    );
                                                })}
                                            </Select>
                                        </div>
                                    </Col>
                                    <Col xs={12} sm={6} md={3} lg={2}>
                                        <div className="filter-item">
                                            <Text strong style={{ color: darkMode ? 'var(--dark-text)' : undefined }}>
                                                Ay
                                            </Text>
                                            <Select
                                                value={filters.month}
                                                onChange={(value) => handleFilterChange('month', value)}
                                                style={{
                                                    width: '100%',
                                                    backgroundColor: darkMode ? 'var(--dark-bg)' : undefined,
                                                    borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                                    color: darkMode ? 'var(--dark-text)' : undefined
                                                }}
                                                className={darkMode ? 'dark-select' : ''}
                                            >
                                                {monthNames.map((month, index) => (
                                                    <Option key={index + 1} value={index + 1}>{month}</Option>
                                                ))}
                                            </Select>
                                        </div>
                                    </Col>
                                </>
                            );
                        } else {
                            return (
                                <Col xs={24} sm={12} md={6} lg={4}>
                                    <div className="filter-item">
                                        <Text strong style={{ color: darkMode ? 'var(--dark-text)' : undefined }}>
                                            Tarih Aralığı
                                        </Text>
                                        <RangePicker
                                            value={filters.dateRange}
                                            onChange={(dates) => handleFilterChange('dateRange', dates)}
                                            style={{
                                                width: '100%',
                                                backgroundColor: darkMode ? 'var(--dark-bg)' : undefined,
                                                borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                                color: darkMode ? 'var(--dark-text)' : undefined
                                            }}
                                            placeholder={['Başlangıç', 'Bitiş']}
                                            format="DD.MM.YYYY"
                                            className={darkMode ? 'dark-date-picker' : ''}
                                            locale={{
                                                lang: {
                                                    locale: 'tr_TR',
                                                    placeholder: 'Tarih seçin',
                                                    rangePlaceholder: ['Başlangıç', 'Bitiş'],
                                                    today: 'Bugün',
                                                    now: 'Şimdi',
                                                    backToToday: 'Bugüne dön',
                                                    ok: 'Tamam',
                                                    clear: 'Temizle',
                                                    month: 'Ay',
                                                    year: 'Yıl',
                                                    timeSelect: 'Zaman seç',
                                                    dateSelect: 'Tarih seç',
                                                    monthSelect: 'Ay seç',
                                                    yearSelect: 'Yıl seç',
                                                    decadeSelect: 'On yıl seç',
                                                    yearFormat: 'YYYY',
                                                    dateFormat: 'DD.MM.YYYY',
                                                    dayFormat: 'DD',
                                                    dateTimeFormat: 'DD.MM.YYYY HH:mm:ss',
                                                    monthFormat: 'MMMM',
                                                    monthBeforeYear: true,
                                                    previousMonth: 'Önceki ay (PageUp)',
                                                    nextMonth: 'Sonraki ay (PageDown)',
                                                    previousYear: 'Önceki yıl (Control + left)',
                                                    nextYear: 'Sonraki yıl (Control + right)',
                                                    previousDecade: 'Önceki on yıl',
                                                    nextDecade: 'Sonraki on yıl',
                                                    previousCentury: 'Önceki yüzyıl',
                                                    nextCentury: 'Sonraki yüzyıl',
                                                },
                                                timePickerLocale: {
                                                    placeholder: 'Zaman seçin',
                                                },
                                            }}
                                        />
                                    </div>
                                </Col>
                            );
                        }
                    })()}

                    {/* Arama */}
                    <Col xs={24} sm={12} md={6} lg={4}>
                        <div className="filter-item">
                            <Text strong style={{ color: darkMode ? 'var(--dark-text)' : undefined }}>
                                Arama
                            </Text>
                            <Input
                                placeholder="Arama yapın..."
                                value={filters.search}
                                onChange={(e) => handleFilterChange('search', e.target.value)}
                                prefix={<SearchOutlined style={{ color: darkMode ? 'var(--dark-text-muted)' : undefined }} />}
                                allowClear
                                style={{
                                    backgroundColor: darkMode ? 'var(--dark-bg)' : undefined,
                                    borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                    color: darkMode ? 'var(--dark-text)' : undefined
                                }}
                                className={darkMode ? 'dark-input' : ''}
                            />
                        </div>
                    </Col>

                    {/* Aksiyon Butonları */}
                    <Col xs={24} sm={24} md={6} lg={12}>
                        <div className="filter-actions" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Space wrap>
                                <Button
                                    onClick={clearAllFilters}
                                    icon={<ClearOutlined />}
                                    className={darkMode ? 'dark-button' : ''}
                                    style={{
                                        backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                                        borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                        color: darkMode ? 'var(--dark-text)' : undefined
                                    }}
                                >
                                    Temizle
                                </Button>
                                <Dropdown
                                    menu={{
                                        items: [
                                            {
                                                key: 'excel',
                                                icon: <FileExcelOutlined />,
                                                label: 'Excel (.xlsx)',
                                                onClick: () => exportToExcel(filteredAndSortedData)
                                            },
                                            {
                                                key: 'print',
                                                icon: <PrinterOutlined />,
                                                label: 'Yazdır',
                                                onClick: () => printTable(filteredAndSortedData, { title: demirbasTabs.find(t => t.key === activeTab)?.label || 'Demirbaş Yönetimi Verileri', message })
                                            }
                                        ]
                                    }}
                                    trigger={['click']}
                                >
                                    <Button
                                        icon={<DownloadOutlined />}
                                        className={darkMode ? 'dark-button' : ''}
                                        style={{
                                            backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                                            borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                            color: darkMode ? 'var(--dark-text)' : undefined
                                        }}
                                    >
                                        Dışa Aktar
                                    </Button>
                                </Dropdown>
                                <Dropdown
                                    open={columnSelectDropdownOpen}
                                    onOpenChange={setColumnSelectDropdownOpen}
                                    trigger={['click']}
                                    dropdownRender={() => (
                                        <div style={{
                                            padding: '2px',
                                            minWidth: '250px',
                                            maxHeight: '400px',
                                            overflowY: 'auto',
                                            backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : '#fff',
                                            border: `1px solid ${darkMode ? 'var(--dark-border)' : '#d9d9d9'}`,
                                            borderRadius: '6px',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                                        }}>
                                            <div style={{ marginBottom: '8px', paddingBottom: '8px', borderBottom: `1px solid ${darkMode ? 'var(--dark-border)' : '#d9d9d9'}` }}>
                                                <Checkbox
                                                    checked={selectedColumns.length === columns.length && columns.length > 0}
                                                    indeterminate={selectedColumns.length > 0 && selectedColumns.length < columns.length}
                                                    onChange={(e) => handleSelectAllColumns(e.target.checked)}
                                                    style={{ color: darkMode ? 'var(--dark-text)' : undefined }}
                                                >
                                                    <Text strong style={{ color: darkMode ? 'var(--dark-text)' : undefined }}>
                                                        Tümünü Seç
                                                    </Text>
                                                </Checkbox>
                                            </div>
                                            <Checkbox.Group
                                                value={selectedColumns}
                                                onChange={handleColumnSelectionChange}
                                                style={{ width: '100%' }}
                                            >
                                                <Space direction="vertical" style={{ width: '100%' }}>
                                                    {columns.map(column => (
                                                        <Checkbox
                                                            key={column.key}
                                                            value={column.key}
                                                            style={{
                                                                width: '100%',
                                                                color: darkMode ? 'var(--dark-text)' : undefined
                                                            }}
                                                        >
                                                            {column.title}
                                                        </Checkbox>
                                                    ))}
                                                </Space>
                                            </Checkbox.Group>
                                        </div>
                                    )}
                                >
                                    <Button
                                        icon={<EyeOutlined />}
                                        className={darkMode ? 'dark-button' : ''}
                                        style={{
                                            backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                                            borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                            color: darkMode ? 'var(--dark-text)' : undefined
                                        }}
                                    >
                                        Sütun Seç
                                    </Button>
                                </Dropdown>
                                {/* Chatbot */}
                                <Chatbot />
                            </Space>
                        </div>
                    </Col>
                </Row>
            </Card>

            {/* Tablo */}
            <Card
                className={`fatura-content-card ${darkMode ? 'dark-card' : ''}`}
                style={{
                    marginTop: 16,
                    backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : undefined,
                    borderColor: darkMode ? 'var(--dark-border)' : undefined
                }}
            >
                <div>
                    {/* Aksiyon Butonları */}
                    <div className="fatura-actions" style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
                        <Space>
                            <Button
                                icon={<BarChartOutlined />}
                                onClick={handlePivotRedirect}
                                disabled={loading || data.length === 0}
                                className={darkMode ? 'dark-button' : ''}
                                style={{
                                    backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                                    borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                    color: darkMode ? 'var(--dark-text)' : undefined
                                }}
                            >
                                Pivot Tabloya Git
                            </Button>
                        </Space>
                    </div>

                    {/* Tablo */}
                    {error && (
                        <Alert
                            message="Hata"
                            description={error}
                            type="error"
                            showIcon
                            style={{ marginBottom: 16 }}
                        />
                    )}

                    <Table
                        dataSource={paginatedData}
                        loading={loading}
                        rowKey={(record) => record.key_banka_fatura_kes || record.id || record.No || Math.random()}
                        scroll={{ x: 'max-content' }}
                        className={darkMode ? 'dark-table' : ''}
                        pagination={{
                            current: pagination.current,
                            total: filteredAndSortedData.length,
                            pageSize: pagination.pageSize,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (total, range) =>
                                `Toplam ${total} kayıt (${range[0]}-${range[1]})`,
                            onChange: handlePageChange,
                            onShowSizeChange: handlePageChange,
                            pageSizeOptions: ['10', '25', '50', '100']
                        }}
                        size="small"
                        style={{
                            width: '100%',
                            backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : undefined
                        }}
                        columns={visibleColumns.map((column, index) => {
                            const activeTabData = demirbasTabs.find(tab => tab.key === activeTab);
                            const hasActionButtons = activeTabData && activeTabData.actionButtons;
                            
                            const columnKey = column.key || '';
                            const normalizedKey = columnKey.replace(/\s+/g, '_').toUpperCase(); 
                            const actionButtonsToShow = hasActionButtons ? (
                                activeTabData.actionButtons[columnKey] || 
                                activeTabData.actionButtons[normalizedKey] || 
                                activeTabData.actionButtons[columnKey.toLowerCase()] ||
                                activeTabData.actionButtons[columnKey.toUpperCase()] ||
                                []
                            ) : [];
                            const shouldShowActionButtons = actionButtonsToShow.length > 0;

                            return {
                                ...column,
                                filters: undefined,
                                filterDropdown: false,
                                title: (
                                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                        <span style={{ flex: 1, cursor: 'pointer' }}>{column.title}</span>
                                        <Dropdown
                                            dropdownRender={() => getFilterDropdown(column)}
                                            trigger={['click']}
                                            placement="bottomRight"
                                        >
                                            <Button
                                                type="text"
                                                size="small"
                                                style={{
                                                    marginLeft: 4,
                                                    color: filteredInfo[column.key] ? (darkMode ? 'var(--dark-text)' : '#1890ff') : '#ccc'
                                                }}
                                            >
                                                <FilterOutlined style={{ fontSize: 12 }} />
                                            </Button>
                                        </Dropdown>
                                    </div>
                                ),
                                onHeaderCell: () => ({
                                    onContextMenu: (e) => {
                                        e.preventDefault();
                                        removeColumn(column.key);
                                    },
                                    onClick: () => {
                                        if (column.sortable) {
                                            handleSort(column.key);
                                        }
                                    }
                                }),
                                dataIndex: column.key,
                                key: column.key,
                                align: 'center',
                                width: 40,
                                ellipsis: {
                                    showTitle: true
                                },
                                render: (text, record) => (
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        {shouldShowActionButtons ? (
                                            <ActionButtons
                                                record={record}
                                                actionButtons={actionButtonsToShow}
                                                darkMode={darkMode}
                                                buttonLayout="vertical"
                                            />
                                        ) : (
                                            <span>{formatCellValue(column.key, text)}</span>
                                        )}
                                    </div>
                                ),
                                sorter: column.sortable ? (a, b) => {
                                    const aVal = a[column.key];
                                    const bVal = b[column.key];
                                    if (column.isNumber) {
                                        const numA = parseValue(aVal);
                                        const numB = parseValue(bVal);
                                        return numA - numB;
                                    } else if (column.isDateColumn) {
                                        let dateA = null, dateB = null;

                                        if (aVal) {
                                            const dateValueA = String(aVal).trim();
                                            if (dateValueA.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
                                                dateA = dayjs(dateValueA, 'DD.MM.YYYY');
                                            } else if (dateValueA.match(/^\d{4}-\d{2}-\d{2}$/)) {
                                                dateA = dayjs(dateValueA, 'YYYY-MM-DD');
                                            } else {
                                                dateA = parseDate(aVal);
                                            }
                                        }

                                        if (bVal) {
                                            const dateValueB = String(bVal).trim();
                                            if (dateValueB.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
                                                dateB = dayjs(dateValueB, 'DD.MM.YYYY');
                                            } else if (dateValueB.match(/^\d{4}-\d{2}-\d{2}$/)) {
                                                dateB = dayjs(dateValueB, 'YYYY-MM-DD');
                                            } else {
                                                dateB = parseDate(bVal);
                                            }
                                        }

                                        if (!dateA && !dateB) return 0;
                                        if (!dateA || !dateA.isValid()) return -1;
                                        if (!dateB || !dateB.isValid()) return 1;
                                        return dateA.isBefore(dateB) ? -1 : dateA.isAfter(dateB) ? 1 : 0;
                                    } else {
                                        return String(aVal || "").localeCompare(String(bVal || ""), 'tr');
                                    }
                                } : false,
                                showSorterTooltip: false
                            };
                        })}
                    />
                </div>
            </Card>

            {/* Form Modal */}
            {formModalOpen && (
                <Modal
                    title={modalTitle}
                    open={formModalOpen}
                    onCancel={closeFormModal}
                    footer={null}
                    destroyOnHidden={true}
                    width={800}
                >
                    <div style={{ maxHeight: 600, overflowY: 'auto' }}>
                        <FormikForm
                            formId={currentFormId}
                            uniqueValue={currentUniqueValue}
                            uniqueField={formUniqueField}
                            tableName={formTableName}
                            onSuccess={handleFormSuccess}
                        />
                    </div>
                </Modal>
            )}

            {/* Detail Modal */}
            {detailModalOpen && (
                <Modal
                    title={modalTitle}
                    open={detailModalOpen}
                    onCancel={closeDetailModal}
                    footer={null}
                    destroyOnHidden={true}
                    width={1200}
                    style={{ top: 20 }}
                    className="demirbas-detail-modal"
                >
                    <div style={{ maxHeight: 600, overflowY: 'auto', fontSize: 12 }}>
                        {detailError && (
                            <Alert
                                message="Hata"
                                description={detailError}
                                type="error"
                                showIcon
                                style={{ marginBottom: 16 }}
                            />
                        )}

                        <Table
                            dataSource={detailData}
                            loading={detailLoading}
                            rowKey={(record) => record.dosyaID || record.id || Math.random()}
                            scroll={{ x: 'max-content' }}
                            className={darkMode ? 'dark-table' : ''}
                            size="small"
                            style={{
                                width: '100%',
                                backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : undefined
                            }}
                            pagination={{
                                pageSize: 10,
                                showSizeChanger: true,
                                showQuickJumper: true,
                                showTotal: (total, range) =>
                                    `${range[0]}-${range[1]} / ${total} kayıt`,
                                pageSizeOptions: ['10', '25', '50', '100']
                            }}
                        >
                            {detailData && detailData.length > 0 && Object.keys(detailData[0]).map((key, index) => {
                                const totalColumns = Object.keys(detailData[0]).length;
                                const isLastColumn = index === totalColumns - 1;

                                return (
                                    <Table.Column
                                        key={key}
                                        title={key}
                                        dataIndex={key}
                                        width={50}
                                        ellipsis={{
                                            showTitle: true
                                        }}
                                        render={(text, record) => (
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                {isLastColumn ? (
                                                    <Space size="small">
                                                        {modalButtonType === 'main-table' ? (
                                                            <>
                                                                <Button
                                                                    type="default"
                                                                    size="small"
                                                                    icon={<EditOutlined />}
                                                                    onClick={() => {
                                                                        handleEditFile(record);
                                                                    }}
                                                                    className={darkMode ? 'dark-button' : ''}
                                                                    style={{
                                                                        backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                                                                        borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                                                        color: darkMode ? 'var(--dark-text)' : undefined
                                                                    }}
                                                                >
                                                                    Düzenle
                                                                </Button>
                                                                <Button
                                                                    type="default"
                                                                    size="small"
                                                                    icon={<PlusOutlined />}
                                                                    onClick={() => handleAddDelivery(record)}
                                                                    className={darkMode ? 'dark-button' : ''}
                                                                    style={{
                                                                        backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                                                                        borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                                                        color: darkMode ? 'var(--dark-text)' : undefined
                                                                    }}
                                                                >
                                                                    Teslimat Ekle
                                                                </Button>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Button
                                                                    type="default"
                                                                    size="small"
                                                                    icon={<EyeOutlined />}
                                                                    onClick={() => handleViewFile(record)}
                                                                    className={darkMode ? 'dark-button' : ''}
                                                                    style={{
                                                                        backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                                                                        borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                                                        color: darkMode ? 'var(--dark-text)' : undefined
                                                                    }}
                                                                >
                                                                    Görüntüle
                                                                </Button>
                                                                <Button
                                                                    type="default"
                                                                    size="small"
                                                                    icon={<EditOutlined />}
                                                                    onClick={() => {
                                                                        handleSubEditFile(record);
                                                                    }}
                                                                    className={darkMode ? 'dark-button' : ''}
                                                                    style={{
                                                                        backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                                                                        borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                                                        color: darkMode ? 'var(--dark-text)' : undefined
                                                                    }}
                                                                >
                                                                    Düzenle
                                                                </Button>
                                                            </>
                                                        )}
                                                    </Space>
                                                ) : (
                                                    <span>{formatCellValue(key, text)}</span>
                                                )}
                                            </div>
                                        )}
                                        sorter={(a, b) => {
                                            const aVal = a[key];
                                            const bVal = b[key];

                                            if (key.toLowerCase().includes('tarih') || key.toLowerCase().includes('tar')) {
                                                let dateA = null, dateB = null;

                                                if (aVal) {
                                                    const dateValueA = String(aVal).trim();
                                                    if (dateValueA.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
                                                        dateA = dayjs(dateValueA, 'DD.MM.YYYY');
                                                    } else if (dateValueA.match(/^\d{4}-\d{2}-\d{2}$/)) {
                                                        dateA = dayjs(dateValueA, 'YYYY-MM-DD');
                                                    } else {
                                                        dateA = dayjs(aVal);
                                                    }
                                                }
                                                if (bVal) {
                                                    const dateValueB = String(bVal).trim();
                                                    if (dateValueB.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
                                                        dateB = dayjs(dateValueB, 'DD.MM.YYYY');
                                                    } else if (dateValueB.match(/^\d{4}-\d{2}-\d{2}$/)) {
                                                        dateB = dayjs(dateValueB, 'YYYY-MM-DD');
                                                    } else {
                                                        dateB = dayjs(bVal);
                                                    }
                                                }
                                                if (!dateA && !dateB) return 0;
                                                if (!dateA || !dateA.isValid()) return -1;
                                                if (!dateB || !dateB.isValid()) return 1;
                                                return dateA.isBefore(dateB) ? -1 : dateA.isAfter(dateB) ? 1 : 0;
                                            }
                                            if (!isNaN(aVal) && !isNaN(bVal)) {
                                                return Number(aVal) - Number(bVal);
                                            }
                                            return String(aVal).localeCompare(String(bVal), 'tr');
                                        }}
                                    />
                                );
                            })}
                        </Table>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default DemirbasYonetimiPage;